import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_DISCLAIMER =
    "HPM is not a law firm and does not provide legal advice. All AI-generated " +
    "analysis, eligibility reads, and demand letters are informational only. " +
    "Consult a licensed attorney in your jurisdiction before acting on anything " +
    "you read here. Past results do not guarantee future outcomes.";

export const Footer = ({ disclaimer }) => {
    const [cfg, setCfg] = useState(null);
    useEffect(() => {
        axios.get(`${API}/config/public`).then((r) => setCfg(r.data)).catch(() => {});
    }, []);
    const mailingAddress = cfg?.mailing_address || "";
    const businessName = cfg?.business_name || "Higher Power Movement";
    const contactEmail = cfg?.contact_email || "hello@alphacovenantholdings.com";

    return (
        <footer
            className="border-t-2 border-white/20 bg-[#0a0a0a] mt-32"
            data-testid="hpm-footer"
        >
            <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-12 grid grid-cols-1 md:grid-cols-5 gap-8">
                <div className="md:col-span-1">
                    <div className="font-anton text-4xl uppercase leading-none mb-3">
                        HIGHER<br />POWER<br />MOVEMENT
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                        The System Is Rigged. We Level It.
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-4">
                        A product of<br />
                        <span className="text-white/70">{businessName}</span>
                    </p>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                        PRODUCT
                    </div>
                    <ul className="space-y-2 font-sans text-sm">
                        <li><Link to="/hpm-submit" className="hover:text-white text-white/70">Submit Case</Link></li>
                        <li><Link to="/hpm-search" className="hover:text-white text-white/70">Search Law</Link></li>
                        <li><Link to="/hpm-dashboard" className="hover:text-white text-white/70">My Cases</Link></li>
                        <li><Link to="/hpm-blog" className="hover:text-white text-white/70">Legal Blog</Link></li>
                        <li><Link to="/hpm-cases/solved" className="hover:text-white text-white/70">Wall of Wins</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00] mb-3">
                        FREE TOOLS
                    </div>
                    <ul className="space-y-2 font-sans text-sm">
                        <li><Link to="/hpm-expungement" className="hover:text-white text-white/70" data-testid="footer-tool-expungement">Expungement Checker</Link></li>
                        <li><Link to="/demand-letter" className="hover:text-white text-white/70" data-testid="footer-tool-demand">Demand Letter</Link></li>
                        <li><Link to="/courtroom" className="hover:text-white text-white/70 text-[#FFCC00]">HPM Courtroom</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                        FOR FIRMS
                    </div>
                    <ul className="space-y-2 font-sans text-sm">
                        <li><Link to="/hpm-law-firms" className="hover:text-white text-white/70">Join as a Firm</Link></li>
                        <li><Link to="/hpm-firms" className="hover:text-white text-white/70">Find a Firm</Link></li>
                        <li><Link to="/firm-portal" className="hover:text-white text-white/70">Firm Portal</Link></li>
                        <li><Link to="/hpm-widget" className="hover:text-white text-white/70">Embed Widget</Link></li>
                    </ul>
                </div>
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">
                        CONTACT
                    </div>
                    <ul className="space-y-2 font-sans text-sm">
                        <li>
                            <Link to="/contact" className="hover:text-white text-white/70" data-testid="footer-contact-link">
                                Contact Us
                            </Link>
                        </li>
                        <li>
                            <a
                                href={`mailto:${contactEmail}`}
                                className="inline-flex items-center gap-2 hover:text-white text-white/70"
                                data-testid="footer-contact-email"
                            >
                                <Mail size={12} /> {contactEmail}
                            </a>
                        </li>
                        {mailingAddress && (
                            <li
                                className="inline-flex items-start gap-2 text-white/60 font-mono text-[11px]"
                                data-testid="footer-mailing-address"
                            >
                                <MapPin size={12} className="mt-0.5 shrink-0" />
                                <span className="leading-relaxed">{mailingAddress}</span>
                            </li>
                        )}
                        <li className="pt-2">
                            <Link to="/privacy" className="hover:text-white text-white/70" data-testid="footer-privacy-link">
                                Privacy Policy
                            </Link>
                        </li>
                        <li>
                            <Link to="/terms" className="hover:text-white text-white/70" data-testid="footer-terms-link">
                                Terms of Service
                            </Link>
                        </li>
                        <li>
                            <Link to="/refund-policy" className="hover:text-white text-white/70" data-testid="footer-refund-link">
                                Refund Policy
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
            <div
                className="border-t border-white/10 px-6 sm:px-10 py-6 text-[11px] font-mono text-white/40 leading-relaxed max-w-[1600px] mx-auto"
                data-testid="footer-legal-disclaimer"
            >
                <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFCC00]">
                    NOT LEGAL ADVICE
                </div>
                <div className="mb-3">
                    {FALLBACK_DISCLAIMER}
                    {disclaimer ? ` ${disclaimer}` : ""}
                </div>
                <div>
                    © {new Date().getFullYear()} {businessName} · All Rights Reserved
                </div>
            </div>
        </footer>
    );
};

export default Footer;
